import React, { useState, useCallback } from "react";
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { MARKETING_NAV_PRODUCT_LINKS, MARKETING_NAV_RESOURCES_LINKS } from "../data/marketingHeaderNav";
import { scrollToAnchorBelowHeader } from "../utils/scrollToAnchor";
import { useAuth } from "../context/EnterpriseAuthContext";

/**
 * Navigates using hash routes on the home page or path-only routes.
 * When already on `/`, still scrolls to the target section (Router may not remount).
 *
 * @param {import("react-router-dom").NavigateFunction} navigate - Router navigate.
 * @param {{ to?: string, href?: string }} item - Link definition.
 */
function goToNavItem(navigate, item) {
  if (item.to) {
    if (item.to.startsWith("/#")) {
      const hash = item.to.slice(2);
      navigate({ pathname: "/", hash: `#${hash}` });
      window.setTimeout(() => {
        scrollToAnchorBelowHeader(hash);
      }, 120);
    } else {
      navigate(item.to);
    }
  } else if (item.href) {
    window.open(item.href, "_blank", "noopener,noreferrer");
  }
}

/**
 * Sticky marketing header: centered desktop nav with dropdowns and a mobile drawer. Signed-out
 * visitors see "Sign in" / "Get started"; signed-in users see a single "Open app" button to
 * /home (marketing pages stay reachable for them, nothing redirects).
 *
 * @returns {React.ReactElement}
 */
const MarketingSiteHeader = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktopNav = useMediaQuery(theme.breakpoints.up("md"));
  const { state: authState } = useAuth();
  const isSignedIn = Boolean(authState?.isAuthenticated && !authState?.requiresPasswordReset);

  const [productAnchor, setProductAnchor] = useState(null);
  const [resourcesAnchor, setResourcesAnchor] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const goLogin = useCallback(() => navigate("/login"), [navigate]);
  const goHome = useCallback(() => navigate("/"), [navigate]);
  const goApp = useCallback(() => {
    setMobileOpen(false);
    navigate("/home");
  }, [navigate]);

  const closeDropdowns = useCallback(() => {
    setProductAnchor(null);
    setResourcesAnchor(null);
  }, []);

  const handleNavItem = useCallback(
    (item) => {
      goToNavItem(navigate, item);
      closeDropdowns();
      setMobileOpen(false);
    },
    [navigate, closeDropdowns]
  );

  const navButtonSx = {
    textTransform: "none",
    fontWeight: 500,
    color: "text.primary",
    px: 1.25,
    minHeight: 44,
    borderRadius: 2,
    "&:hover": { backgroundColor: "var(--q-accent-tint, rgba(74, 0, 224, 0.06))" },
  };

  const primaryButtonSx = {
    textTransform: "none",
    fontWeight: 600,
    px: { xs: 2, sm: 2.5 },
    borderRadius: 999,
    boxShadow: "0 10px 28px rgba(74,0,224,0.22)",
    whiteSpace: "nowrap",
  };

  const dropdownPaperSx = {
    mt: 1,
    minWidth: 220,
    borderRadius: 2,
    boxShadow: "0 12px 40px rgba(15,23,42,0.12)",
    border: "1px solid",
    borderColor: "divider",
  };

  const desktopNav = (
    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ px: { md: 0, lg: 2 } }}>
      <Button
        color="inherit"
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18, opacity: 0.7 }} />}
        onClick={(e) => setProductAnchor(e.currentTarget)}
        sx={navButtonSx}
        aria-haspopup="true"
        aria-expanded={Boolean(productAnchor)}
      >
        Product
      </Button>
      <Menu
        anchorEl={productAnchor}
        open={Boolean(productAnchor)}
        onClose={() => setProductAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: dropdownPaperSx }}
        MenuListProps={{ dense: true }}
      >
        {MARKETING_NAV_PRODUCT_LINKS.map((item) => (
          <MenuItem key={item.label} onClick={() => handleNavItem(item)}>
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      <Button color="inherit" onClick={() => handleNavItem({ to: "/use-cases" })} sx={navButtonSx}>
        Use cases
      </Button>

      <Button
        color="inherit"
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18, opacity: 0.7 }} />}
        onClick={(e) => setResourcesAnchor(e.currentTarget)}
        sx={navButtonSx}
        aria-haspopup="true"
        aria-expanded={Boolean(resourcesAnchor)}
      >
        Resources
      </Button>
      <Menu
        anchorEl={resourcesAnchor}
        open={Boolean(resourcesAnchor)}
        onClose={() => setResourcesAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: dropdownPaperSx }}
        MenuListProps={{ dense: true }}
      >
        {MARKETING_NAV_RESOURCES_LINKS.map((item) => (
          <MenuItem key={item.label} onClick={() => handleNavItem(item)}>
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );

  return (
    <>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          // Theme token so the bar follows light and dark mode (signed-in users keep their theme here).
          backgroundColor: "var(--q-surface, #FFFFFF)",
          backdropFilter: "blur(16px)",
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              py: { xs: 1, sm: 1.25 },
              minHeight: { xs: 56, sm: 64 },
              gap: 1,
            }}
          >
            <Box
              onClick={goHome}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                flexShrink: 0,
                mr: { xs: 1, lg: 2 },
              }}
            >
              <img src="/QuillAI-Logo.png" alt="Quill AI logo" style={{ height: 34, marginRight: 10 }} />
              <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: "-0.02em" }}>
                Quill AI
              </Typography>
            </Box>

            {isDesktopNav ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  minWidth: 0,
                  px: 1,
                }}
              >
                {desktopNav}
              </Box>
            ) : (
              <Box sx={{ flex: 1 }} />
            )}

            <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="center" sx={{ flexShrink: 0 }}>
              {isSignedIn ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={goApp}
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                  sx={primaryButtonSx}
                >
                  Open app
                </Button>
              ) : (
                <>
                  <Button
                    color="inherit"
                    onClick={goLogin}
                    sx={{
                      textTransform: "none",
                      fontWeight: 500,
                      display: { xs: "none", sm: "inline-flex" },
                      ...navButtonSx,
                    }}
                  >
                    Sign in
                  </Button>
                  <Button variant="contained" color="primary" onClick={goLogin} sx={primaryButtonSx}>
                    Get started
                  </Button>
                </>
              )}
              <IconButton
                color="inherit"
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
                sx={{ display: { xs: "inline-flex", md: "none" }, ml: 0.5 }}
              >
                <MenuIcon />
              </IconButton>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: { width: "min(100vw - 48px, 360px)", pt: 1 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Menu
          </Typography>
          <IconButton onClick={() => setMobileOpen(false)} aria-label="Close menu">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List sx={{ py: 1 }} component="nav">
          <ListSubheader disableSticky sx={{ lineHeight: "32px", fontWeight: 700, color: "text.secondary" }}>
            Product
          </ListSubheader>
          {MARKETING_NAV_PRODUCT_LINKS.map((item) => (
            <ListItemButton key={item.label} onClick={() => handleNavItem(item)}>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 500 }} />
            </ListItemButton>
          ))}
          <ListItemButton onClick={() => handleNavItem({ to: "/use-cases" })}>
            <ListItemText primary="Use cases" primaryTypographyProps={{ fontWeight: 500 }} />
          </ListItemButton>
          <ListSubheader disableSticky sx={{ lineHeight: "32px", fontWeight: 700, color: "text.secondary", mt: 1 }}>
            Resources
          </ListSubheader>
          {MARKETING_NAV_RESOURCES_LINKS.map((item) => (
            <ListItemButton key={item.label} onClick={() => handleNavItem(item)}>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 500 }} />
            </ListItemButton>
          ))}
        </List>
        <Divider />
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {isSignedIn ? (
            <Button variant="contained" color="primary" fullWidth onClick={goApp} sx={{ textTransform: "none", fontWeight: 600, borderRadius: 999 }}>
              Open app
            </Button>
          ) : (
            <>
              <Button variant="outlined" color="inherit" fullWidth onClick={goLogin} sx={{ textTransform: "none", fontWeight: 600 }}>
                Sign in
              </Button>
              <Button variant="contained" color="primary" fullWidth onClick={goLogin} sx={{ textTransform: "none", fontWeight: 600, borderRadius: 999 }}>
                Get started
              </Button>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default MarketingSiteHeader;
